import Input from "components/common/input";
import SelectComponent from "components/common/select";
import ToggleButton from "components/common/togglebutton";
import { userInitialValues, userSchema } from "constants/user";
import { Formik, ErrorMessage } from "formik";
import React, { useEffect, useState } from "react";
import AsyncSelect from "components/common/select/AsyncSelect";
import { getCounties, getStates } from "containers/policies/api";
import {
  useDebouncedCountiesDataCallback,
  useDebouncedStatesDataCallback,
} from "utils/debouncedHelper";
import { initialMetaForTable } from "constants/common";
import { formatLocation, sortData } from "utils/common";

const userTypes = [
  { label: "Program Coordinator", value: "program_coordinator" },
  { label: "Housing Navigator", value: "housing_navigator" },
  { label: "CSS Manager", value: "case_manager" },
  { label: "Executive", value: "executive" },
  { label: "Accounting", value: "accounting" },
];

const AddUserForm = ({
  handleClose,
  handleSubmition,
  currentUserEdit,
  defaultUserType,
  currentUserType,
}) => {
  const [statesList, setStatesList] = useState([]);
  const [countiesList, setCountiesList] = useState([]);
  const debouncedStatesDataCallback = useDebouncedStatesDataCallback();
  const debouncedCountiesDataCallback = useDebouncedCountiesDataCallback();
  const isEditMode = Boolean(currentUserEdit);

  const normalizeType = (type) => {
    if (!type) return "";
    return type.replace(/([a-z])([A-Z])/g, "$1_$2").toLowerCase();
  };

  const selectedType =
    currentUserType || (isEditMode ? normalizeType(currentUserEdit.type) : "");

  const initialValues = isEditMode
    ? {
        user: {
          first_name: currentUserEdit.first_name || "",
          last_name: currentUserEdit.last_name || "",
          email: currentUserEdit.email || "",
          password: "",
          password_confirmation: "",
          location: currentUserEdit.location || "",
          status: currentUserEdit.status || "active",
          admin: currentUserEdit.admin || false,
          user_states_attributes: currentUserEdit.states
            ? currentUserEdit.states.map((s) => ({
                id: s.user_state_id,
                value: s.id || s.value,
                label: s.name || s.label,
              }))
            : [],
          user_counties_attributes: currentUserEdit.counties
            ? currentUserEdit.counties.map((c) => ({
                id: c.user_county_id,
                value: c.id || c.value,
                label: c.name || c.label,
              }))
            : [],
        },
        user_type: selectedType,
      }
    : {
        ...userInitialValues,
        user_type: selectedType,
      };

  const fetchStates = async () => {
    try {
      const result = await getStates();
      if (result?.states) {
        const data = result?.states;
        const sorted = sortData(data);
        setStatesList(
          sorted.map((state) => ({
            value: state.id,
            label: state.name.toUpperCase(),
          })),
        );
      }
    } catch (error) {
      console.error("Error fetching states:", error);
    }
  };

  const fetchCounties = async (stateIds) => {
    if (!stateIds || stateIds.length === 0) {
      setCountiesList([]);
      return;
    }
    try {
      const result = await getCounties({
        ...initialMetaForTable,
        state_id: stateIds,
      });
      if (result?.counties) {
        const data = result?.counties;
        const sorted = sortData(data);
        setCountiesList(
          sorted.map((county) => ({
            value: county.id,
            label: formatLocation(county.name),
          })),
        );
      }
    } catch (error) {
      console.error("Error fetching counties:", error);
    }
  };

  useEffect(() => {
    fetchStates();
  }, []);

  useEffect(() => {
    if (isEditMode && currentUserEdit.states) {
      const stateIds = currentUserEdit.states.map((s) => s.id || s.value);
      fetchCounties(stateIds);
    }
  }, [currentUserEdit]);

  return (
    <>
      <Formik
        initialValues={initialValues}
        validationSchema={userSchema}
        enableReinitialize
        onSubmit={async (values, { setSubmitting, setErrors }) => {
          const originalStateAttrIds = isEditMode
            ? (currentUserEdit.states || [])
                .map((s) => s.user_state_id)
                .filter(Boolean)
            : [];
          const originalCountyAttrIds = isEditMode
            ? (currentUserEdit.counties || [])
                .map((c) => c.user_county_id)
                .filter(Boolean)
            : [];

          const currentStateAttrs = values.user.user_states_attributes || [];
          const currentCountyAttrs = values.user.user_counties_attributes || [];

          const currentStateIds = currentStateAttrs
            .map((s) => s.id)
            .filter(Boolean);
          const currentCountyIds = currentCountyAttrs
            .map((c) => c.id)
            .filter(Boolean);

          const removedStateAttrs = isEditMode
            ? originalStateAttrIds
                .filter((id) => !currentStateIds.includes(id))
                .map((id) => ({ id, _destroy: true }))
            : [];
          const removedCountyAttrs = isEditMode
            ? originalCountyAttrIds
                .filter((id) => !currentCountyIds.includes(id))
                .map((id) => ({ id, _destroy: true }))
            : [];

          const payload = {
            user: {
              ...values.user,
              user_states_attributes: [
                ...currentStateAttrs.map((s) => ({
                  id: s.id,
                  state_id: s.value,
                  status: "active",
                })),
                ...removedStateAttrs,
              ],
              user_counties_attributes: [
                ...currentCountyAttrs.map((c) => ({
                  id: c.id,
                  county_id: c.value,
                  status: "active",
                })),
                ...removedCountyAttrs,
              ],
            },
            user_type: values.user_type,
          };
          handleSubmition(payload, setSubmitting, setErrors);
        }}
      >
        {({ handleChange, values, handleSubmit, setFieldValue }) => (
          <div className="question-modal">
            <form className="form-main" onSubmit={handleSubmit}>
              <hr className="custom-modal-line mt-0" />

              <div className="input-flex row">
                <div className="col-sm-6">
                  <ToggleButton
                    label="Status"
                    name="user.status"
                    value={values.user.status === "active" ? 1 : 0}
                    handleChange={(name, value) => {
                      setFieldValue(name, value ? "active" : "inactive");
                    }}
                  />
                  <div className="pt-2">
                    <Input
                      label="First Name"
                      isRequired
                      placeholder="e.g. John"
                      type="text"
                      value={values.user.first_name}
                      name="user.first_name"
                      handleChange={handleChange}
                      error={
                        <ErrorMessage
                          className="error-message"
                          component="p"
                          name="user.first_name"
                        />
                      }
                    />
                  </div>
                </div>
                <div className="col-sm-6">
                  <ToggleButton
                    label="Admin"
                    name="user.admin"
                    value={values.user.admin ? 1 : 0}
                    handleChange={(name, value) => {
                      setFieldValue(name, value ? true : false);
                    }}
                  />
                  <div className="pt-2">
                    <Input
                      label="Last Name"
                      isRequired
                      placeholder="e.g. Doe"
                      type="text"
                      value={values.user.last_name}
                      name="user.last_name"
                      handleChange={handleChange}
                      error={
                        <ErrorMessage
                          className="error-message"
                          component="p"
                          name="user.last_name"
                        />
                      }
                    />
                  </div>
                </div>
              </div>

              <Input
                label="Email"
                isRequired
                placeholder="e.g. john.doe@example.com"
                type="email"
                value={values.user.email}
                name="user.email"
                handleChange={handleChange}
                error={
                  <ErrorMessage
                    className="error-message"
                    component="p"
                    name="user.email"
                  />
                }
              />

              {!isEditMode && (
                <>
                  <Input
                    label="Password"
                    isRequired={!isEditMode}
                    placeholder="Enter password"
                    type="password"
                    value={values.user.password}
                    name="user.password"
                    handleChange={handleChange}
                    error={
                      <ErrorMessage
                        className="error-message"
                        component="p"
                        name="user.password"
                      />
                    }
                  />

                  <Input
                    label="Confirm Password"
                    isRequired={!isEditMode}
                    placeholder="Confirm password"
                    type="password"
                    value={values.user.password_confirmation}
                    name="user.password_confirmation"
                    handleChange={handleChange}
                    error={
                      <ErrorMessage
                        className="error-message"
                        component="p"
                        name="user.password_confirmation"
                      />
                    }
                  />
                </>
              )}

              <Input
                label="Location"
                placeholder="e.g. New York"
                type="text"
                value={values.user.location}
                name="user.location"
                handleChange={handleChange}
                error={
                  <ErrorMessage
                    className="error-message"
                    component="p"
                    name="user.location"
                  />
                }
              />

              <AsyncSelect
                label="State"
                isMulti={true}
                options={statesList}
                loadOptions={(inputValue) =>
                  new Promise((resolve) => {
                    debouncedStatesDataCallback(inputValue, resolve);
                  })
                }
                selectedValue={values.user.user_states_attributes}
                placeholder="Select State"
                handleChange={(arr) => {
                  setFieldValue("user.user_states_attributes", arr || []);
                  const stateIds = arr ? arr.map((s) => s.value) : [];
                  fetchCounties(stateIds);
                  setFieldValue("user.user_counties_attributes", []);
                }}
                error={
                  <ErrorMessage
                    className="error-message"
                    component="p"
                    name="user.user_states_attributes"
                  />
                }
              />

              <AsyncSelect
                label="County"
                isMulti={true}
                options={countiesList}
                loadOptions={(inputValue) =>
                  new Promise((resolve) => {
                    debouncedCountiesDataCallback(
                      inputValue,
                      values.user.user_states_attributes?.map((s) => s.value) ||
                        [],
                      resolve,
                    );
                  })
                }
                selectedValue={values.user.user_counties_attributes}
                placeholder="Select County"
                handleChange={(arr) =>
                  setFieldValue("user.user_counties_attributes", arr || [])
                }
                error={
                  <ErrorMessage
                    className="error-message"
                    component="p"
                    name="user.user_counties_attributes"
                  />
                }
                isDisabled={!values.user.user_states_attributes?.length}
              />

              <SelectComponent
                isRequired
                label="User Type"
                selectedValue={values.user_type}
                options={userTypes}
                placeholder="Select User Type"
                handleChange={(obj) => {
                  setFieldValue("user_type", obj.value);
                }}
                error={
                  <ErrorMessage
                    className="error-message"
                    component="p"
                    name="user_type"
                  />
                }
              />

              <div className="action gap-2">
                <button
                  className="secondary-btn w-50"
                  type="button"
                  onClick={handleClose}
                >
                  Cancel
                </button>
                <input
                  className="primary-btn w-50"
                  type="submit"
                  name="submit"
                  value={isEditMode ? "Edit User" : "Add User"}
                />
              </div>
            </form>
          </div>
        )}
      </Formik>
    </>
  );
};

export default AddUserForm;
